// ShineText.tsx
import React, { ReactNode, useEffect, useRef, useState } from 'react';

import styles from './style.module.css';

export interface AnimationOptions {
  duration?: number; // 动画时长（秒）
  iterations?: number | 'infinite'; // 动画重复次数
  timingFunction?: string; // 动画时间函数
  shineWidth?: number; // 闪光宽度（像素）
}

export interface ShineTextProps {
  children: ReactNode;
  className?: string;
  style?: React.CSSProperties;
  textColor?: string;
  shineColor?: string;
  useShine?: boolean;
  animation?: AnimationOptions;
}

const DEFAULT_ANIMATION: AnimationOptions = {
  duration: 5,
  iterations: 'infinite',
  timingFunction: 'linear',
  shineWidth: 6,
};

const ShineEffect: React.FC<{
  content: string;
  shineColor?: string;
  animation?: AnimationOptions;
}> = ({ content, shineColor, animation = DEFAULT_ANIMATION }) => {
  const shineStyle = {
    '--shine-color': shineColor,
    '--duration': `${animation.duration}s`,
    '--iteration-count': animation.iterations,
    '--timing-function': animation.timingFunction,
    '--shine-width': `${animation.shineWidth}px`,
  } as React.CSSProperties;

  return (
    <div
      className={styles.shineEffect}
      style={shineStyle}
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: content }}
    />
  );
};

const ShineText: React.FC<ShineTextProps> = ({
  children,
  className = '',
  style,
  textColor,
  shineColor,
  useShine = true,
  animation = DEFAULT_ANIMATION,
}) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [content, setContent] = useState<string>('');

  useEffect(() => {
    if (textRef.current) {
      setContent(textRef.current.innerHTML);
    }
  }, [children]);

  if (!useShine) {
    return (
      <div className={className} style={style}>
        {children}
      </div>
    );
  }

  const combinedStyle = {
    ...style,
    '--text-color': textColor,
  } as React.CSSProperties;

  return (
    <div className={`${styles.shineWrapper} ${className}`} style={combinedStyle}>
      <div ref={textRef} className={styles.shineContent}>
        {children}
      </div>
      <ShineEffect content={content} shineColor={shineColor} animation={animation} />
    </div>
  );
};

export default ShineText;
